// src/components/YourModule/PurchaseOrderModule/AllSalesOrders.jsx

import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import {
  Search,
  ChevronDown,
  ChevronRight,
  FileText,
  AlertCircle,
  Package,
  Eye,
  Printer,
  Archive,
  Clock,
  MapPin,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Info,
  RotateCcw,
  History,
  ShieldAlert,
  ShoppingCart,
  Truck,
  Hash,
  Plus,
  RefreshCw,
  HelpCircle,
} from "lucide-react";

import Loader from "../../../loader/Loader";
import Pagination from "../../Pagination";
import { domain, domainAccounting } from "../../../../security";
import {
  selectUserID,
  selectLocationId,
  selectWithSalesQuotation,
} from "../../../../redux/IchthusSlice";

import SOApprovalActions from "./SOApprovalActions";
import RejectSOItemModal from "./RejectSOItemModal";
import ViewSOItemRejectionModal from "./ViewSOItemRejectionModal";
import SODeliveryOrdersModal from "./SODeliveryOrdersModal";
import { SalesOrderPrint } from "./SalesOrderPrint";
import RevertTransactionModal from "../../Transactions/Modals/RevertTransactionModal";
import AddSalesOrder from "./AddSalesOrder";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const calculateOriginalTotal = (so) => {
  if (!so?.salesOrderItems) return 0;
  return so.salesOrderItems.reduce(
    (acc, item) => acc + (item.quantity || 0) * (item.unitPrice || 0),
    0,
  );
};

const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase() || "";
  let color = "bg-gray-50 text-gray-600 border-gray-200";
  let icon = <Info size={11} />;

  if (s === "approved") {
    color = "bg-green-50 text-green-700 border-green-200";
    icon = <CheckCircle size={11} />;
  } else if (s === "closed") {
    color = "bg-blue-50 text-blue-700 border-blue-200";
    icon = <Archive size={11} />;
  } else if (s === "pending approval" || s === "pending") {
    color = "bg-amber-50 text-amber-700 border-amber-200";
    icon = <Clock size={11} />;
  } else if (s === "declined" || s === "rejected") {
    color = "bg-red-50 text-red-700 border-red-200";
    icon = <XCircle size={11} />;
  } else if (s === "draft") {
    color = "bg-slate-100 text-slate-600 border-slate-200";
    icon = <FileText size={11} />;
  } else if (s === "voided") {
    color = "bg-red-100 text-red-700 border-red-300";
    icon = <XCircle size={11} />;
  }

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}
    >
      {icon}
      <span>{status}</span>
    </div>
  );
};

const PriceDisplay = ({ currentTotal, originalTotal }) => {
  const hasChanged = Math.abs(currentTotal - originalTotal) > 0.1;
  return (
    <div className="flex flex-col items-end">
      <span className="font-bold text-gray-900">
        {formatCurrency(currentTotal)}
      </span>
      {hasChanged && (
        <span className="text-xs text-gray-400 line-through">
          {formatCurrency(originalTotal)}
        </span>
      )}
    </div>
  );
};

const SOHistoryModal = ({ isOpen, onClose, currentUserId, onRestored }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const fetchHistory = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const res = await axios.get(`${domain}/api/SalesOrders/history`);
      setHistory(res.data || []);
    } catch {
      toast.error("Failed to load SO history.");
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  const handleRestore = async (id) => {
    try {
      await axios.put(`${domain}/api/SalesOrders/restore/${id}`, {
        userID: currentUserId,
      });
      toast.success("Sales Order restored!");
      fetchHistory();
      onRestored();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to restore.");
    }
  };

  if (!isOpen) return null;

  const filtered = history.filter((s) => {
    const q = search.toLowerCase();
    return (
      (s.salesOrderNumber || "").toLowerCase().includes(q) ||
      (s.customerName || "").toLowerCase().includes(q)
    );
  });
  const currentItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-xl">
              <History size={18} className="text-slate-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">
                Archived Sales Orders
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Restore soft-deleted records
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="px-6 pt-4 pb-3 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by SO number or customer..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : currentItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                <Archive size={22} className="text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">
                No archived records found.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-slate-800 text-sm">
                          {item.salesOrderNumber}
                        </span>
                        <StatusBadge status={item.status} />
                      </div>
                      <p className="text-sm text-slate-500 font-medium truncate">
                        {item.customerName || "Unknown Customer"}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <MapPin size={10} />{" "}
                          {item.locationName || "No location"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />{" "}
                          {formatDate(item.date || item.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className="font-bold text-slate-800 text-sm">
                        {formatCurrency(item.totalAmount)}
                      </span>
                      <button
                        onClick={() => handleRestore(item.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition"
                      >
                        <RotateCcw size={12} /> Restore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="pt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ManualSyncModal = ({ isOpen, onClose, onSync, isSyncing }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }
    onSync(startDate, endDate);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={18} className="text-emerald-600" />
            <h2 className="text-base font-bold text-slate-800">
              Manual Sync to Accounting
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSyncing}
            className="text-slate-400 hover:text-slate-700 transition"
          >
            <XCircle size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 text-blue-800 p-3 rounded-lg text-sm flex items-start gap-2 border border-blue-100 mb-2">
            <Info size={18} className="shrink-0 mt-0.5 text-blue-600" />
            <p>
              <strong>
                Do you want to sync manually in the accounting or did you not
                see the current upload to the accounting?
              </strong>
              <br />
              <br />
              Select a date range below to check for any missed Approved/Closed
              orders and push them.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isSyncing}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                required
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isSyncing}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm"
              />
            </div>
          </div>
          <div className="pt-4 flex justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSyncing}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSyncing}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition disabled:opacity-70"
            >
              {isSyncing ? (
                <>
                  <RefreshCw size={14} className="animate-spin" /> Syncing...
                </>
              ) : (
                "Start Sync"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AllSalesOrders = () => {
  const currentUserId = useSelector(selectUserID);
  const userLocationId = useSelector(selectLocationId);
  const withSalesQuotation = useSelector(selectWithSalesQuotation);
  const [soData, setSoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [expandedSO, setExpandedSO] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [activeTab, setActiveTab] = useState("temp");
  const [allLocationsList, setAllLocationsList] = useState([]);

  // MODAL STATES
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSOToEdit, setSelectedSOToEdit] = useState(null);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isDOModalOpen, setIsDOModalOpen] = useState(false);
  const [selectedSoForDOs, setSelectedSoForDOs] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [itemToReject, setItemToReject] = useState({
    lineItem: null,
    headerId: null,
  });
  const [isViewRejectionOpen, setIsViewRejectionOpen] = useState(false);
  const [itemToView, setItemToView] = useState(null);

  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [selectedSOToRevert, setSelectedSOToRevert] = useState(null);

  const [printData, setPrintData] = useState(null);
  const printSORef = useRef();

  const locations = useMemo(() => {
    if (allLocationsList.length === 0) return ["All"];
    const names = [
      ...new Set(allLocationsList.map((l) => l.locationName)),
    ].sort();
    return [...names, "All"];
  }, [allLocationsList]);

  useEffect(() => {
    if (allLocationsList.length > 0 && selectedLocation === "") {
      if (userLocationId) {
        const userLoc = allLocationsList.find(
          (l) => l.id === parseInt(userLocationId),
        );
        setSelectedLocation(userLoc ? userLoc.locationName : "All");
      } else {
        setSelectedLocation("All");
      }
    }
  }, [allLocationsList, userLocationId, selectedLocation]);

  const handlePrintSO = useReactToPrint({
    content: () => printSORef.current,
    documentTitle: `SO_${printData?.salesOrderNumber || "Document"}`,
  });

  const triggerPrint = (so) => {
    setPrintData(so);
    setTimeout(() => handlePrintSO(), 300);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [soRes, locRes] = await Promise.all([
        axios.get(`${domain}/api/SalesOrders`),
        axios.get(`${domain}/api/Locations`),
      ]);
      setAllLocationsList(locRes.data);

      const orders = soRes.data
        .map((so) => {
          const s = so.status?.toLowerCase() || "";
          const isStaging =
            s === "draft" ||
            s === "declined" ||
            s === "rejected" ||
            s === "pending approval" ||
            s === "pending";
          return { ...so, isStaging };
        })
        .sort((a, b) => b.id - a.id);

      setSoData(orders);
    } catch (error) {
      console.error('There was a problem adding data:', error);
      toast.error("Failed to fetch Sales Orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // SINGLE SYNC FUNCTION (Fired Automatically on Approve)
  const pushSingleOrderToAccounting = async (so) => {
    try {
      /* const currentSubtotal = (so.salesOrderItems || []).reduce((acc, item) => {
        const qty = item.quantity || 0;
        return acc + qty * (item.unitPrice || 0);
      }, 0); */

      const grandTotal = Number(so.totalAmount) || 0;
      const vat = Number(so.vatAmount) || 0;

      const soPayload = [
        {
          soNumber: so.salesOrderNumber || "",
          customerId: so.customerCode || so.customerCode?.toString() || "",
          salesmanCode: `SM-${so.salesmanId || ""}`,
          soDate:
            so.date || so.createdAt
              ? new Date(so.date || so.createdAt).toISOString()
              : new Date().toISOString(),
          paymentTerms: so.paymentTerms || "",
          currency: so.currency || "PHP",
          status: "Approved", // Force status to approved since this runs on approval
          subTotal: grandTotal,
          totalDiscount: Number(so.discountAmount) || 0,
          taxAmount: vat,
          totalAmount: grandTotal,
          reference: so.quoteNumber || "",
          notes: so.remarks || "",
          createdDate: new Date().toISOString(),
          createdBy: currentUserId || 0,
          salesOrderDetails: (so.salesOrderItems || []).map((item) => {
            const qty = item.quantity || 0;
            const itemSubtotal = qty * (item.unitPrice || 0);
            return {
              salesOrderHeaderId: so.id || 0,
              itemCode: item.itemCode || item.productName || "",
              itemDescription: item.productName || "",
              uomName: item.unitName || "",
              brandName: item.brandName || "",
              categoryName: item.categoryName || "",
              quantity: Number(qty),
              taxationCodeId: 0,
              unitPrice: Number(item.unitPrice) || 0,
              subTotal: Number(itemSubtotal),
              totalDiscount: Number(item.discount) || 0,
              taxAmount: 0,
              totalAmount: Number(itemSubtotal),
              notes: item.remarks || "",
            };
          }),
        },
      ];

      await axios.post(`${domainAccounting}/api/SalesOrder/bulk`, soPayload);
      toast.success(
        `Successfully pushed ${so.salesOrderNumber} to Accounting!`,
      );
    } catch (err) {
      console.error(err);
      toast.error(`Failed to push ${so.salesOrderNumber} to Accounting.`);
    }
  };

  const handleManualSync = async (startDate, endDate) => {
    setIsSyncing(true);
    try {
      // 1. Fetch fresh from API + Filter by Date Range
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      // ✅ FIX: Fetch directly from API instead of using local soData state
      const soResponse = await axios.get(`${domain}/api/SalesOrders`);
      const allOrders = soResponse.data || [];

      // ✅ FIX: Check all possible date fields as fallbacks
      const approvedOrdersInRange = allOrders.filter((so) => {
        const soStatus = so.status?.toLowerCase() || "";
        if (soStatus !== "approved" && soStatus !== "partial" && soStatus !== "closed") return false;
        const rawDate = so.date || so.createdAt || so.orderDate;
        if (!rawDate) return false;
        const soDate = new Date(rawDate);
        return soDate >= start && soDate <= end;
      });

      if (approvedOrdersInRange.length === 0) {
        toast.info("No approved orders found within the selected date range.");
        setIsSyncing(false);
        return;
      }

      // 2. Fetch already synced records from Accounting API
      const accountingRes = await axios.get(
        `${domainAccounting}/api/SalesOrder`,
      );
      const records = Array.isArray(accountingRes.data)
        ? accountingRes.data
        : Array.isArray(accountingRes.data?.items)
          ? accountingRes.data.items
          : [];
      const synced = new Set(records.map((s) => s.soNumber).filter(Boolean));

      // 3. Diff and Find unsynced
      const unsyncedLiveSOs = approvedOrdersInRange.filter(
        (so) => so.salesOrderNumber && !synced.has(so.salesOrderNumber),
      );

      if (unsyncedLiveSOs.length === 0) {
        toast.info(
          "All approved orders in this date range are already synced to Accounting.",
        );
        setIsSyncing(false);
        return;
      }

      // 4. Bulk Push Unsynced SOs and DOs
      let syncedCount = 0;
      for (const so of unsyncedLiveSOs) {
        /* const currentSubtotal = (so.salesOrderItems || []).reduce(
          (acc, item) => {
            const qty = item.quantity || 0;
            return acc + qty * (item.unitPrice || 0);
          },
          0,
        ); */

        const soPayload = [
          {
            soNumber: so.salesOrderNumber || "",
            customerId: so.customerCode || so.customerCode?.toString() || "",
            soDate: so.date
              ? new Date(so.date).toISOString()
              : new Date().toISOString(),
            paymentTerms: so.paymentTerms || "",
            currency: so.currency || "PHP",
            status: so.status || "",
            subTotal: Number(so.totalAmount) || 0,
            totalDiscount: Number(so.discountAmount) || 0,
            taxAmount: Number(so.vatAmount) || 0,
            totalAmount: Number(so.totalAmount) || 0,
            reference: so.quoteNumber || "",
            notes: so.remarks || "",
            createdDate: new Date().toISOString(),
            createdBy: currentUserId || 0,
            salesOrderDetails: (so.salesOrderItems || []).map((item) => {
              const qty = item.quantity || 0;
              const itemSubtotal = qty * (item.unitPrice || 0);

              return {
                salesOrderHeaderId: so.id || 0,
                itemCode: item.itemCode || item.productName || "",
                quantity: Number(qty),
                taxationCodeId: 0,
                unitPrice: Number(item.unitPrice) || 0,
                subTotal: Number(itemSubtotal),
                totalDiscount: Number(item.discount) || 0,
                taxAmount: 0,
                totalAmount: Number(itemSubtotal),
                notes: item.remarks || "",
              };
            }),
          },
        ];

        await axios.post(`${domainAccounting}/api/SalesOrder/bulk`, soPayload);

        // Fetch & Sync Delivery Orders associated with this SO (If they exist)
        let deliveryOrders = [];
        try {
          const doRes = await axios.get(
            `${domain}/api/DeliveryOrders/bySo/${so.id}`,
          );
          deliveryOrders = doRes.data?.deliveryOrders || [];
        } catch (doError) {
          console.warn(`Failed to fetch DOs for SO ${so.id}`, doError);
        }

        const activeDOs = deliveryOrders.filter((d) => !d.isVoid);
        if (activeDOs.length > 0) {
          const doPayload = activeDOs.map((doItem) => {
            let calculatedTotal = 0;

            const deliveryOrderDetails = (doItem.items || []).map((line) => {
              const soLine =
                (so.salesOrderItems || []).find(
                  (sItem) =>
                    sItem.productId === line.productId ||
                    sItem.productName === line.productName,
                ) || {};

              const unitPrice = Number(line.unitPrice || soLine.unitPrice || 0);
              const orderedQty = Number(soLine.quantity || 0);
              const deliveredQty = Number(line.quantityDelivered || 0);
              const remainingQty = Math.max(0, orderedQty - deliveredQty);
              const subTotal = deliveredQty * unitPrice;
              calculatedTotal += subTotal;

              return {
                id: 0,
                deliveryOrderHeaderId: 0,
                itemCode:
                  line.itemCode ||
                  soLine.itemCode ||
                  soLine.productName ||
                  "Unknown",
                orderedQty: orderedQty,
                receivedQty: deliveredQty,
                remainingQty: remainingQty,
                unitPrice: unitPrice,
                subTotal: subTotal,
                taxationCodeId: 0,
                totalDiscount: 0,
                taxAmount: 0,
                totalAmount: subTotal,
              };
            });

            return {
              doNumber: doItem.deliveryOrderNumber || `DO-${doItem.id}`,
              salesOrderSONumber: so.salesOrderNumber || "",
              amount: Number(doItem.totalAmount || calculatedTotal),
              doDate: doItem.deliveryDate
                ? new Date(doItem.deliveryDate).toISOString()
                : new Date().toISOString(),
              uploadedFile: "",
              notes: doItem.remarks || doItem.notes || "",
              status: doItem.status || "Closed",
              createdDate: doItem.date
                ? new Date(doItem.date).toISOString()
                : new Date().toISOString(),
              createdBy: currentUserId || 0,
              deliveryOrderDetails: deliveryOrderDetails,
            };
          });

          await axios.post(
            `${domainAccounting}/api/DeliveryOrder/bulk`,
            doPayload,
          );
        }
        syncedCount++;
      }

      toast.success(
        `Successfully synced ${syncedCount} order(s) to Accounting!`,
      );
      setIsSyncModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while syncing to Accounting.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleEditSO = (id) => {
    const soToEdit = soData.find((so) => so.id === id);
    if (soToEdit) {
      setSelectedSOToEdit(soToEdit);
      setIsEditModalOpen(true);
    }
  };

  const openRejectModal = (item, headerId) => {
    setItemToReject({ lineItem: item, headerId });
    setIsRejectModalOpen(true);
  };

  const handleRejectSubmit = async (qty, reason, returnCondition, image) => {
    if (!itemToReject.lineItem || !itemToReject.headerId) return;
    setLoading(true);
    const payload = {
      TotalRejectedQuantity: parseInt(qty),
      ReasonDescription: reason || "",
      ReturnCondition: returnCondition,
      ReasonImage: image || null,
    };

    try {
      await axios.post(
        `${domain}/api/SalesOrders/${itemToReject.headerId}/lineitems/${itemToReject.lineItem.id}/reject`,
        payload,
      );
      toast.success(
        `Rejection recorded! Serials released as ${returnCondition}.`,
      );
      setIsRejectModalOpen(false);
      setItemToReject({ lineItem: null, headerId: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record rejection.");
    } finally {
      setLoading(false);
    }
  };

  const handleSoftDelete = async (id) => {
    const target = soData.find((s) => s.id === id);
    const st = target?.status?.toLowerCase();
    if (st === "closed" || st === "approved" || st === "partial") {
      toast.error("Only draft or declined orders can be archived.");
      return;
    }
    if (
      !window.confirm(
        "Archive this Sales Order? It can be restored from Archived.",
      )
    )
      return;
    try {
      await axios.put(`${domain}/api/SalesOrders/soft-delete/${id}`, {
        userID: currentUserId,
      });
      toast.success("Sales Order archived.");
      if (expandedSO === id) setExpandedSO(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to archive.");
    }
  };

  const openRevertModal = (so) => {
    setSelectedSOToRevert(so);
    setIsRevertModalOpen(true);
  };

  const handleConfirmRevert = async (transactionId, returnCondition) => {
    try {
      await axios.post(`${domain}/api/SalesOrders/revert/${transactionId}`, {
        returnCondition: returnCondition,
        voidBy: currentUserId?.toString() || "System",
      });
      toast.success(
        `Sales Order voided. Serials returned as ${returnCondition}.`,
      );
      setIsRevertModalOpen(false);
      setSelectedSOToRevert(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to void Sales Order.");
    }
  };

  const tabFilteredSOs = soData.filter((so) => {
    const s = so.status?.toLowerCase() || "";
    if (activeTab === "temp") return so.isStaging;
    if (activeTab === "open") return (s === "approved" || s === "partial");
    if (activeTab === "closed") return s === "closed";
    return false;
  });

  const filteredSOs = tabFilteredSOs.filter((so) => {
    const matchesSearch =
      (so.salesOrderNumber || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (so.customerName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (so.quoteNumber || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation =
      selectedLocation === "All" ||
      so.locationName === selectedLocation ||
      selectedLocation === "";
    return matchesSearch && matchesLocation;
  });

  const currentSOs = filteredSOs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const draftCount = soData.filter((s) => s.isStaging).length;
  const approvedCount = soData.filter(
    (s) => (s.status?.toLowerCase() === "approved" || s.status?.toLowerCase() === "partial"),
  ).length;
  const closedCount = soData.filter(
    (s) => s.status?.toLowerCase() === "closed",
  ).length;

  const toggleExpandSO = (id) => setExpandedSO(expandedSO === id ? null : id);
  const canRejectItems = (so) => (so.status?.toLowerCase() === "approved" || so.status?.toLowerCase() === "partial");
  const canVoid = (so) => {
    const s = so.status?.toLowerCase() || "";
    return !so.isVoid && s !== "closed" && s !== "voided" && s !== "shipped";
  };

  const [showSyncToAccountingForm, setShowSyncToAccountingForm] = useState(false);
  const openSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(true);
  }
  const closeSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(false);
  }

  const handleSyncToAccounting = async (dateFrom, dateTo) => {
    if(soData.length <= 0){
      toast.error(`No Orders to Sync.`);
      return
    }

    setLoading(true)
    try {
      const getDateOnly = value => {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      };

      const filtered = soData.filter(i => {
        const date = getDateOnly(i.date);
        const from = getDateOnly(dateFrom);
        const to = getDateOnly(dateTo);

        return date >= from && date <= to;
      });

      // const soPayload = filtered?.filter(i => i.date >= dateFrom && i.date <= dateTo && i.status !== "Draft").map(so => {
      const soPayload = filtered?.filter(i => i.status !== "Draft").map(so => {
        const grandTotal = Number(so.totalAmount) || 0;
        const vat = Number(so.vatAmount) || 0;

        return {
          soNumber: so.salesOrderNumber || "",
          customerId: so.customerCode || "",
          salesmanCode: `SM-${so.salesmanId || ""}`,
          soDate:
            so.date || so.createdAt
              ? new Date(so.date || so.createdAt).toISOString()
              : new Date().toISOString(),
          paymentTerms: so.paymentTerms || "",
          currency: so.currency || "PHP",
          status: so.status || "",
          subTotal: grandTotal,
          totalDiscount: Number(so.discountAmount) || 0,
          taxAmount: vat,
          totalAmount: grandTotal,
          reference: so.quoteNumber || "",
          notes: so.remarks || "",
          createdDate: new Date().toISOString(),
          createdBy: currentUserId || 0,
          salesOrderDetails: (so.salesOrderItems || []).map(item => {
            const qty = Number(item.quantity) || 0;
            const unitPrice = Number(item.unitPrice) || 0;
            const itemSubtotal = qty * unitPrice;

            return {
              salesOrderHeaderId: so.id || 0,
              itemCode: item.itemCode || item.productName || "",
              itemDescription: item.productName || "",
              uomName: item.unitName || "",
              brandName: item.brandName || "",
              categoryName: item.categoryName || "",
              quantity: qty,
              taxationCodeId: 0,
              unitPrice,
              subTotal: itemSubtotal,
              totalDiscount: Number(item.discount) || 0,
              taxAmount: 0,
              totalAmount: itemSubtotal,
              notes: item.remarks || "",
            };
          }),
        };
      });

      await axios.post(`${domainAccounting}/api/SalesOrder/bulk`, soPayload);
      toast.success(
        `Successfully pushed ${soPayload?.length || 0 } to Accounting!`,
      );
    } catch (err) {
      console.error(err);
      toast.error(`Failed to push to Accounting.`);
    } finally {
      setLoading(false)
    }
  };

  return (
    <div className="min-h-screen pb-12 bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      <ManualSyncModal
        isOpen={isSyncModalOpen}
        onClose={() => !isSyncing && setIsSyncModalOpen(false)}
        onSync={handleManualSync}
        isSyncing={isSyncing}
      />

      <RevertTransactionModal
        isOpen={isRevertModalOpen}
        onClose={() => {
          setIsRevertModalOpen(false);
          setSelectedSOToRevert(null);
        }}
        onConfirm={handleConfirmRevert}
        transactionId={selectedSOToRevert?.id}
      />

      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-100 rounded-2xl shadow-sm">
                <ShoppingCart size={22} className="text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 leading-tight">
                  Sales Orders
                </h1>
                <p className="text-gray-500 text-sm">
                  View orders from approved quotations · Create deliveries in
                  Delivery Orders
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* New ? Icon for Manual Sync */}
              <button
                onClick={() => setIsSyncModalOpen(true)}
                title="Sync Manually to Accounting"
                className="p-2 text-indigo-500 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-full transition shadow-sm border border-indigo-200"
              >
                <HelpCircle size={20} />
              </button>

              <button
                onClick={() => openSyncToAccountingForm()}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Sync To Accounting</span>
              </button>

              <button
                onClick={() => setIsHistoryModalOpen(true)}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Archived</span>
              </button>

              {!withSalesQuotation && (
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition shadow-md hover:shadow-lg"
                >
                  <Plus size={16} />
                  Add Sales Order
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-1 mt-5 p-1 bg-gray-100 rounded-xl max-w-max">
            {[
              {
                key: "temp",
                label: "Drafts",
                count: draftCount,
                icon: <FileText size={13} />,
              },
              {
                key: "open",
                label: "Approved",
                count: approvedCount,
                icon: <CheckCircle size={13} />,
              },
              {
                key: "closed",
                label: "Closed",
                count: closedCount,
                icon: <Archive size={13} />,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === tab.key ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.key ? "bg-indigo-50 text-indigo-600" : "bg-gray-200 text-gray-500"}`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-5">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by SO number, customer, or quote #..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all text-sm outline-none"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="relative min-w-[180px]">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 appearance-none bg-white text-sm outline-none"
              >
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc === "All" ? "All Locations" : loc}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="w-10 px-5 py-3.5"></th>
                    <th className="px-5 py-3.5 font-semibold">SO Number</th>
                    <th className="px-5 py-3.5 font-semibold">Customer</th>
                    <th className="px-5 py-3.5 font-semibold">Salesman</th>
                    <th className="px-5 py-3.5 font-semibold">Date</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold text-right">
                      Amount
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentSOs.map((so) => (
                    <React.Fragment key={so.id}>
                      <tr
                        onClick={() => toggleExpandSO(so.id)}
                        className={`hover:bg-indigo-50/30 transition cursor-pointer ${expandedSO === so.id ? "bg-indigo-50/20" : ""}`}
                      >
                        <td className="px-5 py-3.5 text-center">
                          <button className="p-1 hover:bg-gray-200 rounded-lg transition text-gray-400">
                            {expandedSO === so.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-bold text-gray-900">
                            {so.salesOrderNumber}
                          </span>
                          {so.quoteNumber && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              From SQ: {so.quoteNumber}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">
                            {so.customerName}
                          </p>
                          <p className="text-xs text-gray-400">
                            {so.locationName}
                          </p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">
                            {so.salesmanName}
                          </p>
                        </td>
                        <td className="px-5 py-3.5 text-gray-500">
                          {formatDate(so.date || so.createdAt)}
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={so.status} />
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <PriceDisplay
                            currentTotal={so.totalAmount}
                            originalTotal={calculateOriginalTotal(so)}
                          />
                        </td>
                        <td className="px-5 py-3.5">
                          <div
                            className="flex items-center justify-center gap-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {so.isStaging ? (
                              <>
                                <SOApprovalActions
                                  so={so}
                                  currentUserId={currentUserId}
                                  refreshData={fetchData}
                                  onEdit={() => handleEditSO(so.id)}
                                  onDelete={(id) => handleSoftDelete(id)}
                                  onApproveSuccess={() =>
                                    pushSingleOrderToAccounting(so)
                                  }
                                />
                                <button
                                  onClick={() => triggerPrint(so)}
                                  title="Print"
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                >
                                  <Printer size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => triggerPrint(so)}
                                  title="Print"
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                >
                                  <Printer size={16} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedSoForDOs(so);
                                    setIsDOModalOpen(true);
                                  }}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium border border-indigo-100 hover:bg-indigo-100 transition"
                                >
                                  <Truck size={13} /> DOs
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {expandedSO === so.id && (
                        <tr>
                          <td colSpan="7" className="bg-slate-50/70 px-6 py-5">
                            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
                                {[
                                  {
                                    icon: <MapPin size={11} />,
                                    label: "Location",
                                    value: so.locationName || "N/A",
                                  },
                                  {
                                    icon: <Calendar size={11} />,
                                    label: "Order Date",
                                    value: formatDate(so.date || so.createdAt),
                                  },
                                  {
                                    icon: <User size={11} />,
                                    label: "Approver",
                                    value: so.approverName || "N/A",
                                  },
                                  {
                                    icon: <FileText size={11} />,
                                    label: "Quote Ref.",
                                    value: so.quoteNumber || "—",
                                  },
                                ].map((info) => (
                                  <div key={info.label} className="space-y-1">
                                    <span className="text-xs text-gray-400 uppercase font-semibold flex items-center gap-1">
                                      {info.icon} {info.label}
                                    </span>
                                    <p className="text-sm font-medium text-gray-800">
                                      {info.value}
                                    </p>
                                  </div>
                                ))}
                                {so.remarks && (
                                  <div className="col-span-2 md:col-span-4 bg-amber-50 p-3 rounded-lg border border-amber-100">
                                    <span className="text-xs text-amber-700 font-bold uppercase block mb-1">
                                      Notes
                                    </span>
                                    <p className="text-sm text-amber-900">
                                      {so.remarks}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {(so.status?.toLowerCase() === "declined" ||
                                so.status?.toLowerCase() === "rejected") &&
                                so.declineReason && (
                                  <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-100 flex items-start gap-2">
                                    <ShieldAlert
                                      size={14}
                                      className="text-red-500 mt-0.5 shrink-0"
                                    />
                                    <div>
                                      <span className="text-xs font-bold text-red-600 block mb-0.5">
                                        Decline Reason
                                      </span>
                                      <p className="text-sm text-red-700">
                                        {so.declineReason}
                                      </p>
                                    </div>
                                  </div>
                                )}

                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wide flex items-center gap-1.5">
                                  <Package size={14} /> Order Items
                                </h4>
                                {!so.isStaging && (
                                  <button
                                    onClick={() => {
                                      setSelectedSoForDOs(so);
                                      setIsDOModalOpen(true);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium border border-indigo-100 hover:bg-indigo-100 transition"
                                  >
                                    <Truck size={13} /> View Delivery Orders
                                  </button>
                                )}
                              </div>

                              <div className="border border-gray-200 rounded-xl overflow-hidden mb-5">
                                <table className="w-full text-xs text-left text-gray-600">
                                  <thead className="bg-gray-50 text-gray-500 uppercase font-semibold border-b border-gray-100">
                                    <tr>
                                      <th className="px-4 py-2.5">Item Code</th>
                                      <th className="px-4 py-2.5">Product</th>
                                      <th className="px-4 py-2.5 text-center text-gray-600">
                                        Ordered
                                      </th>
                                      <th className="px-4 py-2.5 text-center">
                                        UOM
                                      </th>
                                      <th className="px-4 py-2.5 text-right">
                                        Unit Price
                                      </th>
                                      {!so.isStaging && (
                                        <>
                                          <th className="px-4 py-2.5 text-emerald-600 text-center">
                                            Delivered
                                          </th>
                                          <th className="px-4 py-2.5 text-red-500 text-center">
                                            Rejected
                                          </th>
                                          <th className="px-4 py-2.5 text-amber-600 text-center">
                                            Remaining
                                          </th>
                                        </>
                                      )}
                                      <th className="px-4 py-2.5 text-right">
                                        Line Total
                                      </th>
                                      {canRejectItems(so) && (
                                        <th className="px-4 py-2.5 text-center">
                                          Action
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {(so.salesOrderItems || []).map((item) => {
                                      const qty = item.quantity || 0;
                                      const rejected =
                                        item.rejectedQuantity || 0;
                                      const delivered = item.deliveredQty || 0;
                                      const remaining =
                                        item.remainingQty ??
                                        qty - rejected - delivered;

                                      const originalAmount =
                                        qty * (item.unitPrice || 0);
                                      const rejectedAmt =
                                        rejected * (item.unitPrice || 0);
                                      const adjustedTotal =
                                        rejected > 0
                                          ? originalAmount - rejectedAmt
                                          : item.lineTotal || originalAmount;
                                      const isFullyDelivered =
                                        !so.isStaging &&
                                        remaining <= 0 &&
                                        qty > 0;

                                      return (
                                        <tr
                                          key={item.id}
                                          className={`transition ${isFullyDelivered ? "bg-emerald-50/30" : "hover:bg-gray-50"}`}
                                        >
                                          <td className="px-4 py-2.5">
                                            <span className="inline-flex items-center gap-1 text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                                              <Hash size={10} />
                                              {item.itemCode || "—"}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2.5 font-medium text-gray-900">
                                            {item.productName}
                                          </td>
                                          <td className="px-4 py-2.5 text-center text-gray-700 font-bold">
                                            {qty}
                                          </td>
                                          <td className="px-4 py-2.5 text-center text-gray-500">
                                            {item.unitName || "—"}
                                          </td>
                                          <td className="px-4 py-2.5 text-right">
                                            {formatCurrency(item.unitPrice)}
                                          </td>
                                          {!so.isStaging && (
                                            <>
                                              <td className="px-4 py-2.5 text-emerald-600 text-center font-bold">
                                                {delivered}
                                              </td>
                                              <td className="px-4 py-2.5 text-center font-bold">
                                                {rejected > 0 ? (
                                                  <span className="text-red-500">
                                                    {rejected}
                                                  </span>
                                                ) : (
                                                  <span className="text-gray-300">
                                                    —
                                                  </span>
                                                )}
                                              </td>
                                              <td className="px-4 py-2.5 text-center font-bold">
                                                {remaining > 0 ? (
                                                  <span className="text-amber-600">
                                                    {remaining}
                                                  </span>
                                                ) : (
                                                  <span className="text-emerald-500 text-xs font-semibold">
                                                    Done
                                                  </span>
                                                )}
                                              </td>
                                            </>
                                          )}
                                          <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                                            {formatCurrency(adjustedTotal)}
                                          </td>
                                          {canRejectItems(so) && (
                                            <td className="px-4 py-2.5 text-center">
                                              <div className="flex items-center justify-center gap-1.5">
                                                {rejected > 0 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setItemToView(item);
                                                      setIsViewRejectionOpen(
                                                        true,
                                                      );
                                                    }}
                                                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition"
                                                    title="View rejection"
                                                  >
                                                    <Eye size={14} />
                                                  </button>
                                                )}
                                                {rejected < qty && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      openRejectModal(
                                                        item,
                                                        so.id,
                                                      );
                                                    }}
                                                    className="px-2.5 py-1 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition"
                                                  >
                                                    Reject
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              <div className="flex justify-end">
                                <div className="w-full md:w-72 bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 text-sm">
                                  {so.vatAmount > 0 && (
                                    <div className="flex justify-between text-gray-500">
                                      <span>VAT (12%)</span>
                                      <span className="font-medium text-gray-700">
                                        {formatCurrency(so.vatAmount)}
                                      </span>
                                    </div>
                                  )}
                                  {so.ewtAmount > 0 && (
                                    <div className="flex justify-between text-gray-500">
                                      <span>EWT (1%)</span>
                                      <span className="font-medium text-red-500">
                                        -{formatCurrency(so.ewtAmount)}
                                      </span>
                                    </div>
                                  )}
                                  {(so.salesOrderItems || []).some(
                                    (i) => i.rejectedQuantity > 0,
                                  ) && (
                                    <div className="flex justify-between text-gray-500">
                                      <span>Rejected Amount</span>
                                      <span className="font-medium text-red-500">
                                        -
                                        {formatCurrency(
                                          (so.salesOrderItems || []).reduce(
                                            (sum, i) =>
                                              sum +
                                              (i.rejectedQuantity || 0) *
                                                (i.unitPrice || 0),
                                            0,
                                          ),
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-gray-900">
                                    <span>Grand Total</span>
                                    <PriceDisplay
                                      currentTotal={so.totalAmount}
                                      originalTotal={calculateOriginalTotal(so)}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end gap-2">
                                {!so.isStaging && (
                                  <button
                                    onClick={() => {
                                      setSelectedSoForDOs(so);
                                      setIsDOModalOpen(true);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-semibold hover:bg-indigo-100 transition"
                                  >
                                    <Truck size={13} /> View Delivery Orders
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  {currentSOs.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <AlertCircle size={22} className="text-gray-400" />
                          </div>
                          <p className="text-gray-700 font-semibold">
                            No orders found
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm
                              ? "Try a different search term."
                              : activeTab === "temp"
                                ? "No draft or pending orders."
                                : activeTab === "closed"
                                  ? "No closed orders yet."
                                  : "No approved orders."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden space-y-3">
              {currentSOs.map((so) => (
                <div
                  key={so.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => toggleExpandSO(so.id)}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="p-1.5 bg-indigo-50 rounded-lg shrink-0">
                          <ShoppingCart size={15} className="text-indigo-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-gray-900 text-sm">
                            {so.salesOrderNumber}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {so.customerName}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <StatusBadge status={so.status} />
                        <div className="mt-1.5">
                          <PriceDisplay
                            currentTotal={so.totalAmount}
                            originalTotal={calculateOriginalTotal(so)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedSO === so.id && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => triggerPrint(so)}
                          className="flex-1 py-2 bg-white text-gray-600 rounded-lg text-xs font-medium border border-gray-200 flex items-center justify-center gap-1.5"
                        >
                          <Printer size={14} /> Print
                        </button>
                        {so.isStaging ? (
                          <div className="flex-1">
                            <SOApprovalActions
                              so={so}
                              currentUserId={currentUserId}
                              refreshData={fetchData}
                              onEdit={() => handleEditSO(so.id)}
                              onDelete={handleSoftDelete}
                              onApproveSuccess={() =>
                                pushSingleOrderToAccounting(so)
                              }
                            />
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setSelectedSoForDOs(so);
                                setIsDOModalOpen(true);
                              }}
                              className="flex-1 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium border border-indigo-100 flex items-center justify-center gap-1.5"
                            >
                              <Truck size={14} /> View DOs
                            </button>
                            {canVoid(so) && (
                              <button
                                onClick={() => openRevertModal(so)}
                                className="flex-1 py-2 px-3 bg-red-50 text-red-600 rounded-lg text-xs font-medium border border-red-100 flex items-center justify-center gap-1.5"
                              >
                                <XCircle size={14} /> Void
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {currentSOs.length === 0 && (
                <div className="text-center py-14 text-gray-400 bg-white rounded-2xl border border-gray-100">
                  <AlertCircle size={36} className="mx-auto mb-2 opacity-40" />
                  <p className="font-medium text-gray-500">No orders found.</p>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredSOs.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      <div style={{ display: "none" }}>
        <SalesOrderPrint ref={printSORef} data={printData} />
      </div>

      {isAddModalOpen && (
        <AddSalesOrder
          onClose={() => setIsAddModalOpen(false)}
          refreshData={fetchData}
        />
      )}

      {isEditModalOpen && selectedSOToEdit && (
        <AddSalesOrder
          soData={selectedSOToEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedSOToEdit(null);
          }}
          refreshData={fetchData}
        />
      )}

      <SOHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        currentUserId={currentUserId}
        onRestored={fetchData}
      />
      <RejectSOItemModal
        isOpen={isRejectModalOpen}
        onClose={() => {
          setIsRejectModalOpen(false);
          setItemToReject({ lineItem: null, headerId: null });
        }}
        onSubmit={handleRejectSubmit}
        lineItem={itemToReject.lineItem}
      />
      <ViewSOItemRejectionModal
        isOpen={isViewRejectionOpen}
        onClose={() => {
          setIsViewRejectionOpen(false);
          setItemToView(null);
        }}
        lineItem={itemToView}
      />
      <SODeliveryOrdersModal
        isOpen={isDOModalOpen}
        onClose={() => {
          setIsDOModalOpen(false);
          setSelectedSoForDOs(null);
        }}
        soId={selectedSoForDOs?.id}
        soNumber={selectedSoForDOs?.salesOrderNumber}
      />

      <SyncToAccountingModal 
        isOpen={showSyncToAccountingForm}
        onClose={closeSyncToAccountingForm}
        onConfirm={handleSyncToAccounting}
      />
    </div>
  );
};

const SyncToAccountingModal = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm(dateFrom, dateTo);
    onClose();
  };
  
  const inputClass =
    "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
  const labelClass =
    "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Sync To Accounting
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You are about to sync data to accounting.
            <br />
            <br />
            <strong>Please fill up fields to filter data</strong>
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label className={labelClass}>
                Order Date From
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
            <div>
              <label className={labelClass}>
                Order Date To
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllSalesOrders;
